<template>
  <section
    class="u-position-relative u-spacing-p-l u-display-flex u-flex-direction-column"
  >
    <SvAdGroupVideoProfilePauseCampaignModal
      v-if="showPauseCampaignModal"
      @onApply="({ autoEnable }) => apply(true, autoEnable)"
      @close="closePauseCampaignModal"
    />
    <loader
      :loading="loading"
      class="u-spacing-m-l"
      color="#007cf6"
    />
    <span v-if="!loading">
      <div class="u-display-flex u-spacing-mb-l">
        <div class="u-display-flex u-flex-direction-column">
          <span class="u-font-size-5 u-font-weight-600 u-spacing-mb-m">
            Select a video for this campaign
            <span class="u-color-red-base">*</span>
          </span>
          <RbSelectV2
            class="u-border-radius-s u-border-width-s u-border-all u-border-color-grey-xxx-light u-cursor-pointer u-spacing-mb-m"
            :send-details="true"
            width="560px"
            :options="videoProfiles"
            :search-enabled="true"
            :client-search-key="['name', 'media_id']"
            @change="selectVideoProfile"
          >
            <template slot="trigger">
              <div
                class="u-display-flex u-flex-justify-content-space-between u-spacing-p-s"
              >
                <div class="u-overflow-hidden">
                  <p
                    class="u-font-size-4 u-font-weight-400 u-overflow-hidden u-text-overflow-ellipsis"
                  >
                    {{ selectedVideoProfile.name }}
                  </p>
                </div>
                <rb-icon
                  class="rb-icon--small u-spacing-ml-xs u-color-grey-lighter"
                  icon="caret-down"
                />
              </div>
            </template>
            <template
              slot="item"
              slot-scope="option"
            >
              <div class="u-display-flex u-word-break option-wrapper">
                <div
                  class="u-border-all u-border-width-s u-border-color-grey-xxx-light u-border-radius-s u-spacing-mr-m u-flex-shrink-0 thumbnail-wrapper"
                >
                  <img
                    alt=""
                    :src="option.thumbnail"
                    class="u-width-100 u-height-100 u-object-fit-cover"
                  />
                </div>
                <div>
                  <p
                    class="u-font-size-6 u-font-weight-400 u-overflow-hidden u-text-overflow-ellipsis thumbnail-title-wrapper"
                  >
                    {{ option.name }}
                  </p>
                </div>
              </div>
            </template>
          </RbSelectV2>
          <div
            class="u-border-all u-border-width-s u-border-color-grey-xxx-light u-border-radius-s u-width-560px u-height-320px video-wrapper u-spacing-mb-10"
          >
            <video
              :src="selectedVideoProfile.videoUrl"
              controls
              disablepictureinpicture
              controlsList="nodownload noplaybackrate"
              class="u-width-100 u-height-100 u-object-fit-cover"
            >
              <source
                :src="selectedVideoProfile.videoUrl"
                type="video/mp4"
              />
            </video>
          </div>
        </div>
        <div class="vertical-divider u-spacing-ml-xxl"></div>
        <div
          data-cy="profileTabInfoMessageSection"
          class="u-display-flex u-flex-direction-column u-flex-justify-content-center u-spacing-ph-xxl"
        >
          <div class="u-display-flex u-flex-align-items-center u-spacing-mb-s">
            <rb-icon
              icon="alerts"
              class="rb-icon--small video-icon u-spacing-mr-s u-color-grey-lighter"
            />
            <span
              data-cy="messageSpan"
              class="u-font-size-6 u-font-weight-400"
            >
              Changes will reflect after the review process
            </span>
          </div>
          <span
            data-cy="messageSpan"
            class="u-font-size-6 u-font-weight-400 u-color-grey-lighter u-spacing-mb-m"
          >
            To make a change, the existing video profile must be replaced and
            updated with a new one, which will be active post review.
          </span>
          <span
            data-cy="messageSpan"
            class="u-font-size-6 u-font-weight-400 u-color-grey-lighter"
          >
            If you want to upload a new video please reach out to
            <a href="mailto:support@commerceiq.ai">support@commerceiq.ai</a>.
          </span>
        </div>
      </div>
      <div class="u-display-flex">
        <rb-button
          text="Apply"
          type="filled"
          class="u-flex-align-self-flex-start u-spacing-mr-12"
          size="s"
          :click-fn="onReplaceVideoApply"
        />
        <rb-button
          text="Cancel"
          class="u-flex-align-self-flex-start"
          size="s"
          :click-fn="onClose"
        />
      </div>
    </span>
  </section>
</template>

<script>
import loader from '@/components/basic/loader';
import RbSelectV2 from '@/components/pages/businessInsights/rbSelectV2.vue';
import HttpService from '@/utils/services/http-service';
import modal from '@/components/widgets/modal.vue';
import SvAdGroupVideoProfilePauseCampaignModal from '@/pages/entity_details/configs/walmart/widget_templates/SvAdGroupVideoProfilePauseCampaignModal.vue';

export default {
  components: {
    SvAdGroupVideoProfilePauseCampaignModal,
    loader,
    RbSelectV2,
    modal
  },
  props: {
    campaignId: {
      type: String,
      required: true
    },
    adGroupId: {
      type: String,
      required: true
    },
    currentMediaId: {
      type: Number,
      required: true
    },
    currentAdGroupMediaId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      videoProfiles: null,
      selectedVideoProfile: null,
      shouldPauseCampaign: false,
      showPauseCampaignModal: false
    };
  },
  async created() {
    const campaignInfo =
      this.$store.getters['entityDetails/getCampaignDetails'];
    if (
      campaignInfo.data.state.toLowerCase() === 'live' ||
      campaignInfo.data.state.toLowerCase() === 'scheduled'
    ) {
      this.shouldPauseCampaign = true;
    }
    this.loading = true;
    await this.getVideoProfiles();
    this.selectedVideoProfile = this.videoProfiles[0];
    this.loading = false;
  },
  methods: {
    async getVideoProfiles() {
      try {
        const response = await HttpService.post('FETCH_AMS_ACTIONS_MANAGER', {
          retailer: this.$store.getters.getRetailer,
          advertiserId: this.$route?.query?.advertiser_id,
          filterName: null,
          filterDate: null,
          type: 'MEDIA',
          filters: {
            status: 'LIVED,AVAILABLE'
          }
        });
        this.videoProfiles = response.data.response.filter(
          (profile) => profile.mediaId !== this.currentMediaId
        );
      } catch (e) {
        this.$snackbar.open({
          message: 'Something went wrong. Please perform this action later.',
          duration: 5000
        });
        this.$emit('closePanelMethod');
      }
    },
    selectVideoProfile(option) {
      this.selectedVideoProfile = option;
    },
    closePauseCampaignModal() {
      this.showPauseCampaignModal = false;
    },
    async onReplaceVideoApply() {
      if (this.shouldPauseCampaign) {
        this.showPauseCampaignModal = true;
      } else {
        await this.apply();
      }
    },
    getReplaceVideoPayload(autoEnable) {
      return {
        actionPayload: {
          ad_group_id: this.adGroupId,
          campaign_id: this.campaignId,
          ad_group_media_id: this.currentAdGroupMediaId,
          media_id: this.selectedVideoProfile.mediaId,
          sv_auto_enable: autoEnable
        },
        viewPayload: {
          ad_group_id: this.adGroupId,
          campaign_id: this.campaignId,
          ad_group_media_id: this.currentAdGroupMediaId,
          media_id: this.selectedVideoProfile.mediaId,
          sv_auto_enable: autoEnable
        },
        actionType: `${this.$store.getters.getRetailer}UpdateSvMedia`,
        actionSource: {
          pageUrl: window.location.href
        },
        widget: 'campaign',
        primaryKey: this.campaignId
      };
    },
    getEditCampaignStatusPayload(newStatus) {
      // this logic is true for this use case in a replace video profile flow, not otherwise
      const currentStatus = newStatus === 'Paused' ? 'Live' : 'Paused';
      return {
        widget: 'campaign',
        actionType: `${this.$store.getters.getRetailer}CampaignMetadataChange`,
        actionSource: {
          pageUrl: window.location.href
        },
        primaryKey: this.campaignId,
        actionPayload: {
          advertiser_id: this.$route?.query?.advertiser_id,
          campaign_id: this.campaignId,
          campaignType: 'SV',
          status: newStatus,
          state: newStatus
        },
        viewPayload: {
          advertiser_id: this.$route?.query?.advertiser_id,
          campaign_id: this.campaignId,
          campaignType: 'SV',
          view_old_state: currentStatus,
          state: newStatus,
          status: newStatus
        }
      };
    },
    async replaceVideo() {
      this.loading = true;
      try {
        const response = await HttpService.post('UPDATE_AMS_ACTIONS_MANAGER', [
          this.getReplaceVideoPayload()
        ]);
        if (!response.data.success) {
          throw new Error(response.data.message);
        }
        this.$snackbar.open({
          message:
            'Video has been Replaced Video successfully. Kindly send the campaign for Review from the Banner.',
          duration: 10000
        });
      } catch (e) {
        this.$snackbar.open({
          message:
            e.message ??
            'Something went wrong. Please perform this action later.',
          duration: 10000
        });
      }
      this.loading = false;
    },
    async pauseCampaignAndReplaceVideo(autoEnable) {
      this.loading = true;
      try {
        const response = await HttpService.post(
          'UPDATE_AMS_ACTIONS_MANAGER_VIDEO_PROFILE',
          [
            this.getEditCampaignStatusPayload('Paused'),
            this.getReplaceVideoPayload(autoEnable),
            this.getEditCampaignStatusPayload('Live')
          ]
        );
        if (!response.data.success) {
          throw new Error(response.data.message);
        }
        this.$snackbar.open({
          message:
            'Campaign has been Paused and Video has been Replaced Video successfully. Kindly send the campaign for Review from the Banner.',
          duration: 10000
        });
      } catch (e) {
        this.$snackbar.open({
          message:
            e.message ??
            'Something went wrong. Please perform this action later.',
          duration: 10000
        });
      }
      this.loading = false;
    },
    async apply(shouldPauseCampaign = false, autoEnable = false) {
      this.showPauseCampaignModal = false;
      if (shouldPauseCampaign) {
        await this.pauseCampaignAndReplaceVideo(autoEnable);
      } else {
        await this.replaceVideo();
      }
      this.$emit('closePanelMethod');
    },
    onClose() {
      this.$emit('closePanelMethod');
    }
  }
};
</script>

<style scoped>
.video-icon {
  height: 20px;
  width: 20px;
  &:before {
    font-size: 20px;
  }
}

.vertical-divider {
  background: #e9eaeb;
  width: 1px;
  height: auto;
}

.u-height-320px {
  height: 320px;
}

.u-spacing-mb-10 {
  margin-bottom: 10px;
}

.option-wrapper {
  width: 512px;
  text-wrap: wrap;
}

.thumbnail-wrapper {
  width: 85px;
  height: 64px;
}

.thumbnail-title-wrapper {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}
</style>
